import { AppNode, EdgeData, NodeType } from "@features/builder/graph/types"
import { useGraph } from "@features/builder/graph/graphAtoms"
import { Edge, useReactFlow } from "@xyflow/react"
import { useCallback, useRef } from "react"

import { useCopyPaste } from "@features/builder/graph/contextMenu/useCopyPaste"
import {
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material"
import {
  CancelOutlined,
  ContentCopy,
  ContentPaste,
  DeleteOutline,
  TimelineOutlined,
} from "@mui/icons-material"
import { formatModifierKey } from "@features/keymap/keyShortcutFormatter"
import { KeyBinding } from "@features/keymap/KeyBinding"
import { useElementAdjustedPosition } from "@hooks/useElementAdjustedPosition"
import { shortcuts } from "@features/keymap/shortcuts"
import { useEdgeLock } from "@features/builder/hooks/useEdgeLock"

export type ContextEdgeMenuProps = {
  id: string
  x: number
  y: number
  onCloseClick: () => void
  onOpenEdgesDialog?: () => void
}

export default function ContextEdgeMenu({
  id,
  x,
  y,
  onCloseClick,
  onOpenEdgesDialog,
}: ContextEdgeMenuProps) {
  const { getEdge, getNode, deleteElements, screenToFlowPosition } = useReactFlow<
    AppNode,
    Edge<EdgeData>
  >()
  const { graphId } = useGraph()
  const edge = getEdge(id)
  const { handleCopyNodes, handlePasteNodes } = useCopyPaste(onCloseClick)

  const flowClickPosition = screenToFlowPosition({ x, y })

  const menuRef = useRef<HTMLDivElement>(null)
  const menuPosition = useElementAdjustedPosition({ ref: menuRef, x, y })

  const handleDeleteEdge = useCallback(() => {
    deleteElements({ edges: [{ id }] })
  }, [id, deleteElements])

  const edgeLock = useEdgeLock(edge?.id ?? "")
  if (!edge || !graphId) return

  const source = getNode(edge.source)
  const target = getNode(edge.target)
  if (!source || !target) return

  const isGroupEdge = source.type === NodeType.Group || target.type === NodeType.Group

  const lockedNode = edgeLock ? getNode(edgeLock.lock.node_id) : undefined
  const isLocked = !!edgeLock
  const deleteButtonTooltip = (
    <>
      You can not delete this edge because <strong>{edgeLock?.member.name}</strong> is currently
      editing node <strong>{lockedNode?.data.label}</strong>
    </>
  )

  const handlePaste = () => {
    handlePasteNodes(flowClickPosition)
    onCloseClick()
  }

  const handleCopy = () => {
    handleCopyNodes()
    onCloseClick()
  }

  return (
    <Paper sx={{ position: "fixed", zIndex: 10, ...menuPosition }} ref={menuRef}>
      <MenuList dense>
        <MenuItem onClick={onOpenEdgesDialog} data-testid="pane-menu-create-edges-option">
          <ListItemIcon>
            <TimelineOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Create edges</ListItemText>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
            {formatModifierKey("ctrl")}E
          </Typography>
        </MenuItem>

        {!isGroupEdge && (
          <Tooltip title={edgeLock ? deleteButtonTooltip : ""}>
            <MenuItem
              onClick={handleDeleteEdge}
              disabled={isLocked}
              data-testid="edge-menu-delete-option"
            >
              <ListItemIcon>
                <DeleteOutline fontSize="small" />
              </ListItemIcon>
              <ListItemText>Delete edge</ListItemText>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                DEL
              </Typography>
            </MenuItem>
          </Tooltip>
        )}

        <Divider />

        <MenuItem onClick={handleCopy} data-testid="edge-menu-copy-option">
          <ListItemIcon>
            <ContentCopy fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy</ListItemText>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
            {formatModifierKey("ctrl")}C
          </Typography>
        </MenuItem>

        <MenuItem onClick={handlePaste} data-testid="edge-menu-paste-option">
          <ListItemIcon>
            <ContentPaste fontSize="small" />
          </ListItemIcon>
          <ListItemText>Paste</ListItemText>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
            {formatModifierKey("ctrl")}V
          </Typography>
        </MenuItem>

        <Divider />

        <MenuItem onClick={onCloseClick} data-testid="edge-menu-close-option">
          <ListItemIcon>
            <CancelOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Cancel</ListItemText>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
            ESC
          </Typography>
          <KeyBinding bind={shortcuts["close-edge-menu"]} onKeyDown={onCloseClick} />
        </MenuItem>
      </MenuList>
    </Paper>
  )
}
